import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7dcdedc7"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "providers-list" }
const _hoisted_2 = { class: "provider-header" }
const _hoisted_3 = { class: "provider-key-input q-mt-sm q-ml-lg" }

import { computed, onMounted, ref } from "vue";
import { userApiKeysStore } from "@/hooks";
import { LLMProvider } from "@/types";
import {
  Typography,
  PanelCard,
  TextButton,
  TextInput,
} from "@/components/common";

interface ProviderConfig {
  value: LLMProvider;
  label: string;
  description: string;
  placeholder: string;
}


/**
 * Allows users to manage their API keys and provider preference.
 */
const __default__ = {
  name: "ApiKeysSettings",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const apiKeys = ref<Record<LLMProvider, string>>({
  openai: "",
  anthropic: "",
});

const selectedProvider = ref<LLMProvider>("openai");

const providers: ProviderConfig[] = [
  {
    value: "openai",
    label: "OpenAI",
    description: "Use GPT-4 and other OpenAI models",
    placeholder: "sk-...",
  },
  {
    value: "anthropic",
    label: "Anthropic",
    description: "Use Claude and other Anthropic models",
    placeholder: "sk-ant-...",
  },
];

const hasChanges = computed(() => {
  const hasKeyChanges = Object.values(apiKeys.value).some((key) => key);
  const hasProviderChange =
    selectedProvider.value !== (userApiKeysStore.apiKeys.preferredProvider || "openai");
  return hasKeyChanges || hasProviderChange;
});

const hasExistingKeys = computed(
  () =>
    userApiKeysStore.apiKeys.openaiApiKey ||
    userApiKeysStore.apiKeys.anthropicApiKey
);

// Validate that the selected provider has an API key
const isValid = computed(() => {
  if (!hasChanges.value) return false;

  // Check if the selected provider has either a new key or an existing key
  const provider = selectedProvider.value;
  const hasNewKey = apiKeys.value[provider];
  const hasExistingKey =
    provider === "openai"
      ? userApiKeysStore.apiKeys.openaiApiKey
      : userApiKeysStore.apiKeys.anthropicApiKey;

  return hasNewKey || hasExistingKey;
});

function getProviderKey(provider: LLMProvider): string {
  return apiKeys.value[provider];
}

function setProviderKey(provider: LLMProvider, value: string): void {
  apiKeys.value[provider] = value;
}

function getKeyHint(provider: LLMProvider): string {
  if (selectedProvider.value === provider) {
    return "Required for your selected provider";
  }
  return "Optional";
}

onMounted(async () => {
  await userApiKeysStore.handleLoad();

  // Set initial provider selection
  selectedProvider.value = userApiKeysStore.apiKeys.preferredProvider || "openai";
});

/**
 * Handles saving API keys and provider preference.
 */
async function handleSave(): Promise<void> {
  const payload: Record<string, string> = {
    preferredProvider: selectedProvider.value,
  };

  if (apiKeys.value.openai) {
    payload.openaiApiKey = apiKeys.value.openai;
  }
  if (apiKeys.value.anthropic) {
    payload.anthropicApiKey = apiKeys.value.anthropic;
  }

  await userApiKeysStore.handleSave(payload, {
    success: "Your API keys and provider preference have been saved securely.",
    error: "Unable to save your API keys.",
    onSuccess: () => {
      // Clear the input fields
      apiKeys.value.openai = "";
      apiKeys.value.anthropic = "";
    },
  });
}

/**
 * Handles deleting API keys.
 */
function handleDelete(): void {
  userApiKeysStore.handleDelete({
    success: "Your API keys have been deleted.",
    error: "Unable to delete your API keys.",
    onSuccess: () => {
      // Clear the input fields and reset to default provider
      apiKeys.value.openai = "";
      apiKeys.value.anthropic = "";
      selectedProvider.value = "openai";
    },
  });
}

return (_ctx: any,_cache: any) => {
  const _component_q_radio = _resolveComponent("q-radio")!

  return (_openBlock(), _createBlock(_unref(PanelCard), { title: "API Keys" }, {
    actions: _withCtx(() => [
      _createVNode(_unref(TextButton), {
        label: "Save API Keys",
        disabled: !isValid.value,
        loading: _unref(userApiKeysStore).loading,
        outlined: "",
        "data-cy": "button-save-api-keys",
        onClick: handleSave
      }, null, 8, ["disabled", "loading"]),
      (hasExistingKeys.value)
        ? (_openBlock(), _createBlock(_unref(TextButton), {
            key: 0,
            label: "Delete API Keys",
            loading: _unref(userApiKeysStore).loading,
            color: "negative",
            outlined: "",
            "data-cy": "button-delete-api-keys",
            onClick: handleDelete
          }, null, 8, ["loading"]))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(Typography), {
        el: "p",
        value: "Configure your personal API keys for AI-powered generation tasks. Select your preferred provider and enter the corresponding API key."
      }),
      _createVNode(_unref(Typography), {
        el: "p",
        class: "q-mb-md",
        value: "Your API keys are encrypted and stored securely. Enter new keys to update, or leave blank to keep existing keys."
      }),
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(providers, (provider, index) => {
          return _createElementVNode("div", {
            key: provider.value,
            class: _normalizeClass(["provider-item q-mb-md", { 'provider-selected': selectedProvider.value === provider.value }])
          }, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_q_radio, {
                modelValue: selectedProvider.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedProvider).value = $event)),
                val: provider.value,
                label: `${index + 1}. ${provider.label}`,
                "data-cy": `radio-provider-${provider.value}`,
                class: "provider-radio"
              }, null, 8, ["modelValue", "val", "label", "data-cy"]),
              _createVNode(_unref(Typography), {
                el: "span",
                value: provider.description,
                class: "text-caption text-grey-7 q-ml-lg"
              }, null, 8, ["value"])
            ]),
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(TextInput), {
                "model-value": getProviderKey(provider.value),
                "onUpdate:modelValue": (val) => setProviderKey(provider.value, val),
                label: `${provider.label} API Key`,
                placeholder: provider.placeholder,
                type: "password",
                "data-cy": `input-${provider.value}-key`,
                hint: getKeyHint(provider.value),
                outlined: "",
                dense: ""
              }, null, 8, ["model-value", "onUpdate:modelValue", "label", "placeholder", "data-cy", "hint"])
            ])
          ], 2)
        }), 64))
      ])
    ]),
    _: 1
  }))
}
}

})